import {
  Assets,
  AssetTypeId,
  INCOME_SOURCE_TYPES,
  serializeAssetTypeId,
  deserializeAssetTypeId,
} from './assets.ts';
import type {RetirementStrategy} from './retirement_strategy.ts';
import type {RetirementState} from './retirement_state.ts';
import {income_tax} from './tax.ts';
import {sourceWithdrawals} from './routing.ts';
import type {NormalDist} from './normal.ts';

export class Flow {
  static deserialize(s: string): Flow {
    const j = JSON.parse(s) as Record<string, string | number>;

    return new Flow(
      j.name as string,
      deserializeAssetTypeId(j.source as keyof typeof AssetTypeId),
      deserializeAssetTypeId(j.dest as keyof typeof AssetTypeId),
      j.amount as number,
    );
  }

  _name: string;
  _source: AssetTypeId;
  _dest: AssetTypeId;
  _amount: number;

  constructor(
    name: string,
    source: AssetTypeId,
    dest: AssetTypeId,
    amount: number,
  ) {
    this._name = name;
    this._source = source;
    this._dest = dest;
    this._amount = amount;
  }

  serialize(): string {
    return JSON.stringify({
      name: this.name,
      source: serializeAssetTypeId(this.source),
      dest: serializeAssetTypeId(this.dest),
      amount: this.amount,
    });
  }

  get name(): string {
    return this._name;
  }

  get source(): AssetTypeId {
    return this._source;
  }

  get dest(): AssetTypeId {
    return this._dest;
  }

  get amount(): number {
    return this._amount;
  }

  toString(): string {
    return `Flow ${this._name}: ${this._source}->${this._dest} ${this._amount.toFixed(0)}`;
  }
}

export type FlowStrategy = {
  get name(): string;
  compute_flows(
    rs: RetirementState,
    retirement_strategy: RetirementStrategy,
    noisy_logging: boolean
  ): Flow[];
};

export class EarningFlows implements FlowStrategy {
  // pre-retirement saving: funded from employment income.

  get name(): string {
    return 'Earning Flows';
  }

  compute_flows(
    rs: RetirementState,
    retirement_strategy: RetirementStrategy,
    _noisy_logging: boolean,
  ): Flow[] {
    if (rs.is_retired) {
      return [];
    }

    const flows = [
      new Flow(
        'pre-retirement saving PENSION',
        AssetTypeId.EmploymentIncome,
        AssetTypeId.Pension,
        retirement_strategy.preRetirementSavingsRatesPa.balance(AssetTypeId.Pension)),
      new Flow(
        'pre-retirement saving UNPROTECTED',
        AssetTypeId.EmploymentIncome,
        AssetTypeId.Unprotected,
        retirement_strategy.preRetirementSavingsRatesPa.balance(AssetTypeId.Unprotected)),
      new Flow(
        'pre-retirement saving ISA',
        AssetTypeId.EmploymentIncome,
        AssetTypeId.ISA,
        retirement_strategy.preRetirementSavingsRatesPa.balance(AssetTypeId.ISA)),
    ];
    return flows.filter(f => Math.abs(f.amount) > 0.5);
  }
}

export class GrowthFlows implements FlowStrategy {
  get name(): string {
    return 'Growth Flows';
  }

  compute_flows(
    rs: RetirementState,
    _retirement_strategy: RetirementStrategy,
    _noisy_logging: boolean,
  ): Flow[] {
    const rates = new Map<AssetTypeId, NormalDist>();
    rates.set(AssetTypeId.ISA, rs.market.untaxed_growth_rate);
    rates.set(AssetTypeId.Unprotected, rs.market.taxed_growth_rate);
    rates.set(AssetTypeId.Pension, rs.market.untaxed_growth_rate);
    const flows = [];

    for (const entry of rates.entries()) {
      const asset_type: AssetTypeId = entry[0];

      if (rs.assets.has(asset_type)) {
        const rate_dist = entry[1];
        const rate: number = rate_dist.sample();
        const amount: number = rs.assets.balance(asset_type);
        const growth: number = rate * amount;

        const flow = new Flow(`growth ${asset_type.toString()}`,
          AssetTypeId.Growth,
          asset_type,
          growth,
        );

        flows.push(flow);
      }
    }

    return flows.filter((f: Flow) => Math.abs(f.amount) > 0.5);
  }
}

function flow_from_asset_type(
  asset_type: AssetTypeId,
  withdrawals: Assets,
): Flow {
  const amount = withdrawals.balance(asset_type);

  const flow = new Flow(
    `withdrawal ${asset_type.toString()}`,
    asset_type,
    AssetTypeId.NetIncome,
    amount,
  );

  return flow;
}

export class PensionIncomeFlows implements FlowStrategy {
  _target_net_income: number;

  constructor(target_net_income: number) {
    this._target_net_income = target_net_income;
  }

  get name(): string {
    return 'Pension Income Flows';
  }

  compute_flows(
    rs: RetirementState,
    strategy: RetirementStrategy,
    noisy_logging: boolean,
  ): Flow[] {
    if (!rs.is_retired) {
      return [];
    }

    function calculate_income_tax_and_allowance_charge(
      withdrawals: Assets,
    ): {
        income_tax_on_pension: number;
        tax_free_pension_allowance_charge: number;
      } {
      // Remember that we're not calculating tax on directly earned
      // ((non pension income) because this model is about retirement. The
      // years before retirement only model the accumulation of retirement
      // savings into the three pools.

      const defined_benefit_pension = rs.actual_final_salary_pension_pa;
      const defined_contribution_pension = withdrawals.balance(AssetTypeId.Pension);
      const gross_private_pension = defined_benefit_pension + defined_contribution_pension;

      const remaining_allowance = rs.assets.balance(AssetTypeId.TaxFreePensionAllowance);

      const state_pension: number = rs.actual_state_pension_pa;

      const tax_free_private_pension = Math.min(
        remaining_allowance,
        0.25 * gross_private_pension);

      const taxable_pension = gross_private_pension
                            + state_pension
                            - tax_free_private_pension;

      const income_tax_charge: number = income_tax(taxable_pension);

      return {
        income_tax_on_pension: income_tax_charge,
        tax_free_pension_allowance_charge: tax_free_private_pension,
      };
    }

    function calculate_net_income_and_tax(withdrawals: Assets): Assets {
      const state_pension: number = rs.actual_state_pension_pa;
      const final_salary_pension: number = rs.actual_final_salary_pension_pa;
      const tax_and_charge = calculate_income_tax_and_allowance_charge(withdrawals);
      const income_tax: number = tax_and_charge.income_tax_on_pension;
      const allowance_charge: number = tax_and_charge.tax_free_pension_allowance_charge;
      const national_insurance = 0;
      const total_tax: number = income_tax + national_insurance;

      const pre_tax_income: number
        = state_pension
        + final_salary_pension
        + withdrawals.balance(AssetTypeId.Pension)
        + withdrawals.balance(AssetTypeId.Unprotected)
        + withdrawals.balance(AssetTypeId.ISA);

      const net_income: number = pre_tax_income - total_tax;

      const result = new Assets()
        .credit(AssetTypeId.NetIncome, net_income)
        .credit(AssetTypeId.IncomeTax, income_tax)
        .credit(AssetTypeId.NationalInsurance, national_insurance)
        .credit(AssetTypeId.TaxFreePensionAllowance, allowance_charge);

      return result;
    }

    const is_sufficient = (withdrawals: Assets): boolean => {
      const net_income: number = calculate_net_income_and_tax(
        withdrawals,
      ).balance(AssetTypeId.NetIncome);

      return net_income >= this._target_net_income;
    };

    const withdrawals: Assets = sourceWithdrawals(
      rs, is_sufficient, strategy.early_pension_withdrawal_pa, noisy_logging);

    const flows = [...withdrawals.keys].filter(k => INCOME_SOURCE_TYPES.has(k))
      .map(type => flow_from_asset_type(type, withdrawals));

    if (rs.is_receiving_state_pension) {
      flows.push(
        new Flow(
          'state pension',
          AssetTypeId.StatePension,
          AssetTypeId.NetIncome,
          rs.actual_state_pension_pa));
    }

    if (rs.is_receiving_final_salary_pension) {
      flows.push(
        new Flow(
          'defined benefit pension',
          AssetTypeId.MagicSource,
          AssetTypeId.NetIncome,
          rs.actual_final_salary_pension_pa));
    }

    const income_and_tax: Assets = calculate_net_income_and_tax(withdrawals);
    const net_income: number = income_and_tax.balance(AssetTypeId.NetIncome);

    if (net_income + 0.5 < this._target_net_income) {
      // TODO want to throw FundsExhausted, but catch cannot easily distinguish by TS type.
      throw new Error(
        `Cannot achieve target net income at age=${rs.age}: `
        + `got ${net_income} instead of ${this._target_net_income}`,
      );
    }

    flows.push(
      new Flow(
        'income tax',
        AssetTypeId.NetIncome,
        AssetTypeId.IncomeTax,
        income_and_tax.balance(AssetTypeId.IncomeTax)),
      new Flow(
        'national insurance',
        AssetTypeId.NetIncome,
        AssetTypeId.NationalInsurance,
        income_and_tax.balance(AssetTypeId.NationalInsurance)),
      new Flow(
        'tax free pension allowance consumption',
        AssetTypeId.TaxFreePensionAllowance,
        AssetTypeId.MagicSink,
        income_and_tax.balance(AssetTypeId.TaxFreePensionAllowance)),
    );

    return flows.filter(f => Math.abs(f.amount) > 0.5);
  }
}

export class InRetirementShielding implements FlowStrategy {
  // After retirement, feed ISA with unprotected savings until they're gone.

  get name(): string {
    return 'In Retirement Shielding';
  }

  compute_flows(
    rs: RetirementState,
    retirement_strategy: RetirementStrategy,
    _noisy_logging: boolean,
  ): Flow[] {
    if (!rs.is_retired) {
      return [];
    }

    const unprotected_remaining: number = rs.assets.balance(AssetTypeId.Unprotected);
    const desired_saving: number = retirement_strategy.postRetirementISASavingRatePa;
    const achievable_saving: number = Math.min(desired_saving, unprotected_remaining);

    return achievable_saving > 0.001
      ? [
        new Flow(
          'post-retirement ISA feed',
          AssetTypeId.Unprotected,
          AssetTypeId.ISA,
          achievable_saving),
      ]
      : [];
  }
}

export function get_default_flow_strategies(target_net_income: number) {
  return [
    new EarningFlows(),
    new GrowthFlows(),
    new PensionIncomeFlows(target_net_income),
    new InRetirementShielding(),
  ];
}
